import { useId } from 'react'
import type { Shape, Tense } from '../../shared/momentStyle'
import { cn } from '@/lib/utils'

// The live dot a Now moment carries while it's happening: the logo's colours, turning slowly.
export function LiveDot({ className }: { className?: string }) {
  return <span aria-hidden className={cn('live-dot', className)} />
}

const radio = 'peer sr-only'
const pill =
  'flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-full px-4 text-sm font-medium text-muted-foreground transition-[background-color,color,box-shadow] duration-200 has-checked:bg-brand has-checked:text-brand-foreground has-checked:shadow-paper has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50'

// Then or Now: a memory, dated to when it happened, or something happening as it's written.
export function TenseChoice({ value, onChange }: { value: Tense; onChange: (tense: Tense) => void }) {
  const name = useId()
  return (
    <fieldset className="grid gap-2">
      <legend className="sr-only">Is it a memory or happening now?</legend>
      <div className="flex gap-1 rounded-full bg-secondary p-1">
        <label className={pill}>
          <input
            type="radio"
            name={name}
            className={radio}
            checked={value === 'then'}
            onChange={() => onChange('then')}
          />
          Then · Remembered
        </label>
        <label className={pill}>
          <input
            type="radio"
            name={name}
            className={radio}
            checked={value === 'now'}
            onChange={() => onChange('now')}
          />
          <LiveDot /> Now · Happening
        </label>
      </div>
    </fieldset>
  )
}

const SHAPE_TEXT: Record<Shape, [string, string]> = {
  photo: ['Photo', 'The picture first'],
  words: ['Words', 'A line or two, big'],
  story: ['Story', 'The title and telling'],
}

// How pages draw the moment. Each choice shows a small sketch of itself.
export function ShapeChoice({
  value,
  onChange,
  cover,
  hasPhotos,
}: {
  value: Shape
  onChange: (shape: Shape) => void
  // The first photo, to sketch the photo and story choices with.
  cover?: string
  hasPhotos: boolean
}) {
  const name = useId()
  return (
    <fieldset className="grid gap-3">
      <legend className="mb-1 text-title">Show it as</legend>
      <div className="grid grid-cols-3 gap-2 sm:max-w-md">
        {(['photo', 'words', 'story'] as const).map((shape) => (
          <label
            key={shape}
            className="grid cursor-pointer gap-1.5 rounded-2xl border border-input p-2 text-center text-sm font-medium transition-[border-color,box-shadow] duration-200 has-checked:border-brand has-checked:shadow-[0_0_0_1px_var(--brand)] has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50"
          >
            <input
              type="radio"
              name={name}
              className="sr-only"
              checked={value === shape}
              onChange={() => onChange(shape)}
            />
            <Sketch shape={shape} cover={cover} />
            {SHAPE_TEXT[shape][0]}
            <span className="-mt-1 text-xs font-normal text-muted-foreground">
              {shape === 'photo' && !hasPhotos ? 'Add a photo' : SHAPE_TEXT[shape][1]}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function Sketch({ shape, cover }: { shape: Shape; cover?: string }) {
  const picture = cover ? (
    <img src={cover} alt="" className="size-full object-cover" />
  ) : (
    <span className="size-full bg-muted-foreground/15" />
  )
  if (shape === 'photo') return <span className="flex h-16 overflow-hidden rounded-lg bg-secondary">{picture}</span>
  if (shape === 'words')
    return (
      <span className="flex h-16 flex-col justify-center gap-1 rounded-lg bg-secondary px-2 text-left">
        <span className="bg-logo h-0.5 w-4 rounded-full" />
        <span className="h-1.5 rounded-full bg-foreground/70" />
        <span className="h-1.5 w-4/5 rounded-full bg-foreground/70" />
        <span className="h-1.5 w-1/2 rounded-full bg-foreground/70" />
      </span>
    )
  return (
    <span className="grid h-16 grid-rows-[auto_auto_1fr] gap-1 rounded-lg bg-secondary p-1.5 text-left">
      <span className="h-1.5 w-3/4 rounded-full bg-foreground/80" />
      <span className="h-1 rounded-full bg-foreground/25" />
      <span className="flex overflow-hidden rounded">{picture}</span>
    </span>
  )
}
